import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "reactstrap";

import PaymentOption from "../components/PaymentOption";
import CardWrapperSimple from "../components/CardWrapperSimple";
import PaymentDetails from "../components/PaymentDetails";
import Footer from "../components/Footer";
import CallWait from "../components/CallWait";

import RedpayLogo from "../assets/images/logos/redpay-small.png";

import {
  API_PAYMENT_SELECTION,
  DEFAULT_LANGUAGE,
  DEFAULT_PAY_METHOD,
  DEFAULT_PAY_LOGO,
  API_CALL_TIMEOUT,
  LANGUAGES,
  API_CALL_PAY_TIMEOUT,
  API_GET_PAYMENT_STATUS,
  FEATURE_INTENT_RETRY,
} from "../Constants";

import { testTransactionID, testData } from "../example/exampleTransaction";
import LanguageSelector from "../components/LanguageSelector";
import { captureException } from "@sentry/react";
import axiosWithRetry from "../axios";
import ErrorHandler from "../components/ErrorHandler";
import GatherPayeeData from "../components/Forms/GatherPayeeData";
import PaymentSuccess from "../components/PaymentStatus/PaymentSuccess";
import PaymentCanceled from "../components/PaymentStatus/PaymentCanceled";
import PaymentTimeout from "../components/PaymentStatus/PaymentTimeout";
import { llog } from "../utils/LogUtils";

const PaymentSelectionPage = () => {
  const { t, i18n } = useTranslation();
  const { transactionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const languageParam = searchParams.get("lang");
  const langFound = LANGUAGES.find((lang) => lang.lang === languageParam);
  const language = langFound ? languageParam : DEFAULT_LANGUAGE;

  const navigate = useNavigate();

  const setLanguage = (lang) => {
    searchParams.set("lang", lang);
    setSearchParams(searchParams);
  };

  const [transactionData, setTransactionData] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [selectable, setSelectable] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [showPayeeForm, setShowPayeeForm] = useState(false);

  const [activeMethods, setActiveMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(DEFAULT_PAY_METHOD);
  const [selectedLogo, setSelectedLogo] = useState(DEFAULT_PAY_LOGO);
  const [tosConsent, setTosConsent] = useState(false);

  //  Get transaction status from backend
  useEffect(() => {
    i18n.changeLanguage(language);
    const axios = axiosWithRetry();

    // Provide easy way to test the transaction in development
    if (process.env.NODE_ENV === "development" && transactionId === testTransactionID) {
      setTransactionData(testData);
      setLoading(false);
      return;
    }

    // Get Payment Status

    // Get Payment Information
    const abortController1 = new AbortController();
    const abortController2 = new AbortController();

    if (!transactionStatus) {
      setLoading(true);
      const signal = abortController1.signal;
      axios
        .get(`${API_GET_PAYMENT_STATUS}/${transactionId}/status`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          setTransactionStatus(response.data);
          llog(response.data);
          setLoading(false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setLoading(false);
            setError(error);
            llog(error);
            captureException(error);
          }
        });
    }
    setSelectable(
      transactionStatus?.status === "INITIAL" ||
        transactionStatus?.status === "STARTED" ||
        transactionStatus?.status === "FAILED"
    );

    if (selectable && !transactionData) {
      setLoading(true);
      const signal = abortController2.signal;
      axios
        .get(`${API_PAYMENT_SELECTION}/${transactionId}`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          const data = response.data;
          setTransactionData(data);
          setLoading(false);
          if (!data.payeeFirstname || !data.payeeLastname || !data.payeeEmail) {
            setShowPayeeForm(true);
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setError(error);
            llog(error);
            captureException(error);
            setLoading(false);
          }
        });
    }
    return () => {
      abortController1.abort();
      abortController2.abort();
    };
  }, [transactionId, selectable, transactionStatus, transactionData, i18n, language]);

  // Get Avaliable Payment Methods
  useEffect(() => {
    if (transactionData) {
      const axios = axiosWithRetry();
      const abortController = new AbortController();
      const signal = abortController.signal;
      axios
        .get(`${API_PAYMENT_SELECTION}/${transactionId}/channels`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          const data = response.data;
          const methods = [];
          data.channels?.map((channel) => {
            if (channel.active) {
              const id = channel.channelId;
              if (id.includes(",")) {
                const ids = id.split(",");
                ids.map((id) => {
                  methods.push(id);
                  return id;
                });
              } else {
                methods.push(id);
              }
            }
            return channel;
          });
          setActiveMethods(methods);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            llog(error);
            captureException(error);
          }
        });
    }
  }, [transactionData, transactionId]);

  const handlePayeeData = (firstname, lastname, email) => {
    setShowPayeeForm(false);
    setTransactionData({
      ...transactionData,
      payeeFirstname: firstname,
      payeeLastname: lastname,
      payeeEmail: email,
    });
  };

  // Send selection data to backend and initiate target channel payment.
  const initiatePayment = (e) => {
    e.preventDefault();
    llog("Execute Payment: " + selectedMethod);

    const axios = axiosWithRetry(1);
    const abortController = new AbortController();
    const signal = abortController.signal;
    setRedirecting(true);

    if (selectedMethod === "blik") {
      navigate("/blik/" + transactionId);
      return;
    }

    const transactionType =
      selectedMethod === "blik" || selectedMethod === "card" ? selectedMethod.toUpperCase() : "TRANSFER";

    // TODO: Add support for proper secret and key.
    const selectionData = {
      type: transactionType,
      selectedBank: selectedMethod,
      firstname: transactionData.payeeFirstname,
      lastname: transactionData.payeeLastname,
      email: transactionData.payeeEmail,
    };

    axios
      .post(`${API_PAYMENT_SELECTION}/${transactionId}/select`, selectionData, {
        signal,
        timeout: API_CALL_PAY_TIMEOUT,
      })
      .then((response) => {
        llog(response.data);
        setTimeout(() => {
          window.location.href = response.data.redirectUrl;
        }, 400);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          setError(error);
          llog(error);
          captureException(error);
          setRedirecting(false);
        }
      });
  };

  const cancelPayment = () => {
    // If PI can be retried then we need to cancel the transaction
    if (FEATURE_INTENT_RETRY) {
      const axios = axiosWithRetry();
      const abortController = new AbortController();
      llog("Canceling transaction");
      setLoading(true);
      const signal = abortController.signal;
      axios
        .post(`${API_GET_PAYMENT_STATUS}/${transactionId}/cancel`, { signal, timeout: API_CALL_TIMEOUT })
        .then((response) => {
          setTransactionStatus(response.data);
          llog(response.data);
          setLoading(false);
          redirect2Failed();
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setError(error);
            llog(error);
            captureException(error);
            setLoading(false);
          }
        });
    } else {
      redirect2Failed();
    }
  };

  const redirect2Confirm = () => {
    window.location.href = transactionStatus.merchantConfirmUrl;
  };

  const redirect2Failed = () => {
    if (transactionStatus.merchantFailedUrl === "") {
      window.location.href = transactionStatus.merchantConfirmUrl + "?error=true";
    } else {
      window.location.href = transactionStatus.merchantFailedUrl;
    }
  };

  const getErrorDescription = (error) => {
    const message = error?.response?.data?.message || error?.message || error;
    var description;

    // Client Error
    if (error?.code === "ERR_NETWORK") {
      description = t("error_network");
      return { message, description };
    }

    // Server Response Error
    switch (error?.response?.status) {
      case 429:
        description = t("error_429");
        break;
      default:
        description = t("error_500");
    }

    return { message, description };
  };

  if (error) {
    const { message, description } = getErrorDescription(error);
    return <ErrorHandler message={t("error-message") + message} description={description} />;
  }

  if (loading) return <CallWait message={t("checking-payment")} description={t("call_wait_desc")} />;

  if (redirecting) return <CallWait message={t("redirecting-payment")} description={t("call_wait_desc")} />;

  return (
    <div className='app-wrapper bg-white min-vh-100'>
      <Container>
        <Row className='my-4 align-items-center'>
          <Col md='10' lg='8'>
            <div>
              <img src={RedpayLogo} alt='Redpay logo' className='mx-2 mb-2' width={200} />
              <strong>{t("welcome")}</strong>
            </div>
          </Col>
          <Col md='2' lg='4' className='text-right'>
            <LanguageSelector language={language} setLanguage={setLanguage} />
          </Col>
        </Row>

        {!selectable && (transactionStatus?.status === "SUCCESS" || transactionStatus?.status === "VERIFY") && (
          <PaymentSuccess handleContinue={redirect2Confirm} timeoutEnabled={false} />
        )}
        {!selectable && transactionStatus?.status === "CANCELED_USER" && (
          <PaymentCanceled
            handleContinue={redirect2Failed}
            timeoutEnabled={false}
            description={t("payment-user-canceled-text")}
          />
        )}
        {!selectable && transactionStatus?.status === "CANCELED_ACQ" && (
          <PaymentCanceled
            handleContinue={redirect2Failed}
            timeoutEnabled={false}
            description={t("payment-acq-canceled-text")}
          />
        )}
        {!selectable && transactionStatus?.status === "TIMEOUT" && (
          <PaymentTimeout handleCancel={redirect2Failed} timeoutEnabled={false} />
        )}

        {selectable && showPayeeForm && (
          <GatherPayeeData
            defaultFirstname={transactionData.payeeFirstname}
            defaultLastname={transactionData.payeeLastname}
            defaultEmail={transactionData.payeeEmail}
            setPayeeData={handlePayeeData}
          />
        )}

        {selectable && !showPayeeForm && transactionData && (
          <Row className='mt-4 mb-0'>
            <Col lg='7'>
              <CardWrapperSimple>
                <PaymentOption
                  selectedMethod={selectedMethod}
                  setSelectedMethod={setSelectedMethod}
                  setSelectedLogo={setSelectedLogo}
                  activeMethods={activeMethods}
                />
              </CardWrapperSimple>
            </Col>
            <Col lg='5'>
              <CardWrapperSimple>
                <PaymentDetails
                  transaction={transactionData}
                  paymentName={selectedMethod}
                  paymentLogo={selectedLogo}
                  tosConsent={tosConsent}
                  setTosConsent={setTosConsent}
                  onStartPayment={initiatePayment}
                  cancelEnabled={transactionStatus?.status !== "INITIAL"}
                  onCancelPayment={cancelPayment}
                />
              </CardWrapperSimple>
            </Col>
          </Row>
        )}
      </Container>
      <Footer />
    </div>
  );
};

export default PaymentSelectionPage;
